import {
    Box,
    Checkbox,
    Form,
    Heading,
    OSKIcon,
    OSKThemeType,
    Select,
    Spinner,
    Text,
    TextInput,
    Typography,
} from 'oskcomponents';
import {
    DownloadIntent,
    EULADocument,
    noop,
    OSKGeoJson,
    PaginatedEULADocumentList,
    SigmaAPI,
    useProfileMetadata,
} from 'oskcore';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import { CartCustomization } from '~/molecules';
import { emptyCart, getVisibleItems } from '~/redux/modules/data/cart';
import { RootState } from '~/redux/store';
import { CartWizardStep, CartWizardTemplate } from '~/templates';
import { ArchiveSummarySection } from './ArchiveSummarySection';
import * as yup from 'yup';
import { date_format, getProgramId, parseS3, user_fullname } from '~/utils';
import { setActiveOrderId } from '~/redux/modules/data/orders';
import EulaPicker from '~/organisms/EulaPicker';
import { getFootprints } from '~/redux/modules/data/map';
import { clearOverlayModes } from '~/redux/modules/data/search';
import { ProductPicker } from '~/organisms/ProductPicker';

export type FinalizeArchiveCheckoutProps = {
    /** All items added to the cart */
    enqueuedItems?: Record<string, DownloadIntent | undefined>;
    /** The visible items in the cart. */
    checkoutItems?: Array<DownloadIntent | undefined>;
    /** The footprints that are visible on the map, used for logging */
    visibleFootprints?: OSKGeoJson[];
    /** The search aoi that's visible on the map, used for logging */
    visibleAoi?: any;
} & CartWizardStep;

export const PlaceOrderSchema: yup.SchemaOf<any> = yup.object().shape({
    destination: yup.string().required('Destination is a required field'),
    product_level: yup.string().required('Product Level is a required field'),
});

const BUCKET_STORAGE_ITEM = 'BUCKET_STORAGE_ITEM';
const SELECTED_PRODUCT_STORAGE_ITEM = 'SELECTED_PRODUCT_STORAGE_ITEM';
const SELECTED_EULA_STORAGE_ITEM = 'SELECTED_EULA_STORAGE_ITEM';
const SUBDIRECTORY_CHECKED_ITEM = 'SUBDIRECTORY_CHECKED_ITEM';
const SELECTED_SUBDIRECTORY_TYPE_ITEM = 'SELECTED_SUBDIRECTORY_TYPE_ITEM';

const SubdirectoryTypes = ['Order Name', 'Date'] as const;
type SubdirectoryType = (typeof SubdirectoryTypes)[number];

export const FinalizeArchiveCheckout = ({
    enqueuedItems,
    checkoutItems,
    onWizardBack,
    onWizardNext,
    visibleFootprints,
    visibleAoi,
}: FinalizeArchiveCheckoutProps) => {
    // Parse localstorage goodies
    const defaultBucket = localStorage.getItem(BUCKET_STORAGE_ITEM);
    const defaultProduct = localStorage.getItem(SELECTED_PRODUCT_STORAGE_ITEM);
    const defaultEula = localStorage.getItem(SELECTED_EULA_STORAGE_ITEM) ?? undefined;
    const defaultSubdirectoryChecked = JSON.parse(localStorage.getItem(SUBDIRECTORY_CHECKED_ITEM) ?? 'false');
    const defaultSubdirectoryType =
        (localStorage.getItem(SELECTED_SUBDIRECTORY_TYPE_ITEM) as SubdirectoryType) ?? 'Date';

    const theme = useTheme() as OSKThemeType;
    const programId = getProgramId();
    const dispatch = useDispatch();
    const [user] = useProfileMetadata();
    const [orderName, setOrderName] = useState<string>('');
    const orderNameRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [eulaError, setEulaError] = useState<string>();
    const [bucket, setBucket] = useState<string | null>(defaultBucket);
    const [selectedProduct, setSelectedProduct] = useState<string>(defaultProduct ?? '');
    const [selectedEula, setSelectedEula] = useState<string | undefined>(defaultEula);
    const [submitting, setSubmitting] = useState(false);
    const [isLoadingEulas, setIsLoadingEulas] = useState<boolean>(false);
    const [eulaDocuments, setEulaDocuments] = useState<EULADocument[]>([]);
    const [subdirectoryChecked, setSubdirectoryChecked] = useState<boolean>(defaultSubdirectoryChecked);
    const [selectedSubdirectoryType, setSelectedSubdirectoryType] = useState<SubdirectoryType>(defaultSubdirectoryType);

    const friendlyOrderName = ((selectedSubdirectoryType === 'Date' ? date_format(new Date()) : orderName) ?? '')
        .replace(/\s/g, '-')
        .replace(/\W/g, '-');

    const SubdirectoryPreview = `${bucket}${
        subdirectoryChecked ? `${bucket?.endsWith('/') ? '' : '/'}${friendlyOrderName}` : ''
    }`;

    useEffect(() => {
        setIsLoadingEulas(true);
        SigmaAPI.listEulas({ program: programId })
            .then((response) => response.data)
            .then((data: PaginatedEULADocumentList) => {
                setIsLoadingEulas(false);

                if (data.results && data.count > 0) {
                    setEulaDocuments(data.results);
                }
            })
            .catch((err) => {
                setEulaError(err);
                setIsLoadingEulas(false);
            });
    }, []);

    const ShouldShowEULA = isLoadingEulas ? true : eulaDocuments.length > 0 || eulaError;

    useEffect(() => {
        if (orderNameRef && orderNameRef.current) {
            orderNameRef.current.focus();
        }
    }, [orderNameRef]);

    return (
        <Form
            onSubmit={(data: any) => {
                setSubmitting(true);
                setError(null);
                const { s3Bucket, s3Folder } = parseS3(SubdirectoryPreview);

                if (orderName) {
                    const validOrderNameRegex = /[a-zA-Z0-9][ -._\/a-zA-Z0-9]{0,254}[a-zA-Z0-9]/;
                    const orderNameMatch = (orderName as string).match(validOrderNameRegex);
                    if (!orderNameMatch || orderNameMatch[0] !== orderName) {
                        setError('Invalid order name.');
                        setSubmitting(false);
                        return;
                    }
                }

                if (!selectedProduct) {
                    setError('Must select a product.');
                    setSubmitting(false);
                    return;
                }

                const stacOrderRequest = {
                    collection: selectedProduct,
                    item_ids: checkoutItems?.flatMap((item) => (item && item.fileId ? [item.fileId] : [])) as string[],
                    name: orderName,
                    customer_poc: user_fullname(user),
                    s3_bucket: s3Bucket.trim(),
                    s3_folder: s3Folder.trim(),
                    ...(ShouldShowEULA && !eulaError && selectedEula && { required_license: selectedEula }),
                };

                SigmaAPI.createStacOrder({
                    stacOrderRequest,
                    program: getProgramId(),
                })
                    .then((order) => {
                        const orderId = order.data.order.external_id; //order.data.id;
                        if (orderId) {
                            dispatch(setActiveOrderId(orderId));
                        }

                        // Log some order info for Sentry to pick up
                        console.info('archive order info', {
                            orderName: stacOrderRequest.name,
                            cartItems: JSON.stringify(enqueuedItems),
                            itemIds: JSON.stringify(stacOrderRequest.item_ids),
                            collection: selectedProduct,
                            visibleAoi: JSON.stringify(visibleAoi),
                            visibleFootprints: visibleFootprints?.map((footprint) =>
                                JSON.stringify(footprint.toGeoJSON()),
                            ),
                        });

                        onWizardNext && onWizardNext();

                        // Empty the cart and clear overlay modes on success
                        dispatch(emptyCart());
                        dispatch(clearOverlayModes());
                    })
                    .catch((ex) => {
                        console.error(ex);
                        setError(ex.toString());
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }}
            schema={PlaceOrderSchema}
            style={{ flexDirection: 'row', flexGrow: 1, display: 'flex', width: '100%' }}
        >
            <CartWizardTemplate
                buttons={[
                    { variant: 'primary', inverted: true, label: 'Back', onClick: onWizardBack ?? noop },
                    {
                        variant: 'action',
                        noborder: true,
                        label: 'Place Order',
                        submit: true,
                        onClick: noop,
                    },
                ]}
                style={{
                    width: '100%',
                    overflowY: 'hidden',
                }}
            >
                <Box style={{ width: '100%', overflowY: 'hidden', height: '100%' }} col>
                    <Box center="all" pt={40}>
                        {error && <Text color={theme.colors.red}>{error}</Text>}
                    </Box>
                    <Box pt={0} style={{ overflowY: 'auto' }} grow center="horizontal">
                        {submitting && <Spinner style={{ marginTop: '200px' }} size="Large" variant="Box" />}

                        <Box
                            style={{
                                maxWidth: '1000px',
                                height: '100%',
                                display: submitting ? 'none' : 'flex',
                            }}
                            grow
                        >
                            <Box fg={theme.colors.black900} style={{ height: '770px', maxWidth: '55%' }} col grow>
                                <CartCustomization
                                    title={
                                        <Box center="vertical">
                                            Order Name
                                            <OSKIcon
                                                code="info-circle"
                                                scale={80}
                                                pl={4}
                                                title="Maximum 256 characters. Alpanumeric characters and - . _ / are allowed. Must begin and end with an alphanumeric character."
                                            />
                                        </Box>
                                    }
                                >
                                    <Box pb={10}>
                                        <Text variant="small">Assign a name for this order.</Text>
                                    </Box>
                                    <TextInput
                                        inverted
                                        name="order_name"
                                        placeholder="Order Name"
                                        ref={orderNameRef}
                                        onChange={(e) => {
                                            setOrderName(e.target.value);
                                        }}
                                    />
                                </CartCustomization>
                                <CartCustomization title={'Destination'}>
                                    <Box pb={10} col>
                                        <Text variant="small">Select the destination bucket for file delivery.</Text>
                                        <Text variant="small">This should be a fully qualified s3 uri.</Text>
                                    </Box>
                                    <TextInput
                                        inverted
                                        value={bucket}
                                        onChange={(evt) => {
                                            setBucket(evt.target.value);
                                            localStorage.setItem(BUCKET_STORAGE_ITEM, evt.target.value);
                                        }}
                                        placeholder="s3://bucketname.com/some/folder"
                                        name="destination"
                                    />
                                    <Checkbox
                                        style={{ marginTop: '8px' }}
                                        defaultChecked={subdirectoryChecked}
                                        onChange={(checked) => {
                                            setSubdirectoryChecked(checked);

                                            if (checked) {
                                                localStorage.setItem(SUBDIRECTORY_CHECKED_ITEM, 'true');
                                            } else {
                                                /* If we've unselected the 'add subdirectory' option,
                                                we don't need to hold onto either the checked state or the value. */
                                                localStorage.removeItem(SUBDIRECTORY_CHECKED_ITEM);
                                                localStorage.removeItem(SELECTED_SUBDIRECTORY_TYPE_ITEM);
                                            }
                                        }}
                                    >
                                        <Typography variant="body3" style={{ margin: '0px 8px' }}>
                                            Add subdirectory to destination based on:
                                        </Typography>
                                        <Select
                                            name="subdirectory-type"
                                            inverted
                                            disabled={!subdirectoryChecked}
                                            variant="primary"
                                            items={Object.values(SubdirectoryTypes).map((o) => ({
                                                label: o,
                                                value: o,
                                            }))}
                                            defaultValue={selectedSubdirectoryType}
                                            style={{ width: '100px' }}
                                            onSelect={(option) => {
                                                setSelectedSubdirectoryType(option?.value);

                                                localStorage.setItem(SELECTED_SUBDIRECTORY_TYPE_ITEM, option?.value);
                                            }}
                                        />
                                    </Checkbox>
                                    {subdirectoryChecked && (
                                        <Typography
                                            variant="body3"
                                            style={{ color: theme.colors.black300, marginTop: '4px' }}
                                        >
                                            e.g. {SubdirectoryPreview}
                                        </Typography>
                                    )}
                                </CartCustomization>

                                <CartCustomization title={'Data Product'}>
                                    <Box pb={10} col>
                                        <Text variant="small">Select which product you are ordering.</Text>
                                    </Box>
                                    <ProductPicker
                                        defaultValue={selectedProduct}
                                        onChange={(product) => {
                                            setSelectedProduct(product);
                                            localStorage.setItem(SELECTED_PRODUCT_STORAGE_ITEM, product);
                                        }}
                                    />
                                </CartCustomization>
                                {ShouldShowEULA &&
                                    (isLoadingEulas ? (
                                        <Box style={{ width: '100%' }} center="all">
                                            <Spinner size="Tiny" variant="Box" />
                                        </Box>
                                    ) : (
                                        <CartCustomization title={'End User License Agreement'}>
                                            <Box pb={10} col>
                                                <Text variant="small">
                                                    Select the EULA type that you want delivered. (This will also be set
                                                    in the ENVI headers.)
                                                </Text>
                                            </Box>
                                            <Box col>
                                                {eulaError ? (
                                                    <Typography variant="body3" strong color="red">
                                                        {eulaError.toString()}
                                                    </Typography>
                                                ) : (
                                                    <EulaPicker
                                                        defaultValue={defaultEula}
                                                        eulaDocuments={eulaDocuments}
                                                        onChange={(id) => {
                                                            setSelectedEula(id);
                                                            localStorage.setItem(SELECTED_EULA_STORAGE_ITEM, id);
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </CartCustomization>
                                    ))}
                            </Box>
                            <Box ml={40} col>
                                <Box
                                    w={425}
                                    p={40}
                                    style={{
                                        borderRadius: '8px',
                                        lineHeight: '1.75rem',
                                        position: 'fixed',
                                    }}
                                    bg={theme.colors.black900}
                                    fg={theme.colors.white}
                                    col
                                >
                                    <Heading>Summary</Heading>
                                    <ArchiveSummarySection>
                                        <Box center="vertical" col>
                                            <Text style={{ fontWeight: 'bold', flexGrow: 1 }}>Destination Bucket</Text>
                                            <Text style={{ lineHeight: '1rem' }}>{SubdirectoryPreview ?? 'None'}</Text>
                                        </Box>
                                    </ArchiveSummarySection>
                                    <ArchiveSummarySection>
                                        <Box center="vertical" col>
                                            <Text style={{ fontWeight: 'bold', flexGrow: 1 }}>Data Product</Text>
                                            <Text>{selectedProduct ?? 'None'}</Text>
                                        </Box>
                                    </ArchiveSummarySection>
                                    {selectedEula && (
                                        <ArchiveSummarySection>
                                            <Box center="vertical" col>
                                                <Text style={{ fontWeight: 'bold', flexGrow: 1 }}>EULA</Text>
                                                <Text>
                                                    {eulaDocuments.find(
                                                        (eula) => eula.identifying_value === selectedEula,
                                                    )?.name ?? 'None'}
                                                </Text>
                                            </Box>
                                        </ArchiveSummarySection>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </CartWizardTemplate>
        </Form>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        enqueuedItems: state.data.cart.enqueued, // All items in the cart
        checkoutItems: getVisibleItems(state), // Only items that were selected for final checkout
        visibleFootprints: getFootprints(state),
        visibleAoi: state.data.search.roi,
    };
};

export default connect(mapStateToProps, noop)(FinalizeArchiveCheckout);
